import { supabase } from '../lib/supabase';

/**
 * Service for Student Musicals Discovery Page
 * Handles fetching musicals data with categorization:
 * - Continue Learning (musicals with user progress)
 * - Popular Musicals
 * - Recently Updated Musicals
 * - Recently Added Musicals
 */

export const studentMusicalsDiscoveryService = {
  /**
   * Get musicals the user is currently learning (has progress on songs from these musicals)
   */
  async getContinueLearningMusicals(userId, limit = 12) {
    try {
      // Get unique musical IDs from user's lesson progress via songs
      const { data: progressData, error: progressError } = await supabase?.from('user_lesson_progress')?.select('song_id')?.eq('user_id', userId)?.in('lesson_status', ['started', 'available'])?.limit(50);

      if (progressError) throw progressError;

      // Get song details to find musical IDs
      const songIds = progressData?.map(p => p?.song_id) || [];
      
      if (songIds?.length === 0) return [];

      const { data: songs, error: songsError } = await supabase?.from('songs')?.select('musical_id')?.in('id', songIds);

      if (songsError) throw songsError;

      const musicalIds = [...new Set(songs?.map(s => s?.musical_id)?.filter(Boolean))];

      if (musicalIds?.length === 0) return [];

      // Fetch musical details
      const { data: musicals, error: musicalsError } = await supabase?.from('musicals')?.select('id, title, poster_url, genre, year_premiered, description, composer')?.in('id', musicalIds)?.eq('is_active', true)?.limit(limit);

      if (musicalsError) throw musicalsError;

      return musicals?.map(musical => ({
        id: musical?.id,
        title: musical?.title,
        posterUrl: musical?.poster_url,
        genre: musical?.genre,
        yearPremiered: musical?.year_premiered,
        description: musical?.description,
        composer: musical?.composer
      })) || [];
    } catch (error) {
      console.error('Error fetching continue learning musicals:', error);
      throw error;
    }
  },

  /**
   * Get popular musicals (most favorited)
   */
  async getPopularMusicals(limit = 12) {
    try {
      // Count favorites per musical
      const { data: favorites, error: favError } = await supabase?.from('user_musical_favorites')?.select('musical_id');

      if (favError) throw favError;

      // Count occurrences
      const counts = {};
      favorites?.forEach(fav => {
        counts[fav?.musical_id] = (counts?.[fav?.musical_id] || 0) + 1;
      });

      // Sort by count and get top musical IDs
      const topMusicalIds = // Get more than needed in case some are inactive
      Object.entries(counts)?.sort(([, a], [, b]) => b - a)?.slice(0, limit * 2)?.map(([id]) => id);

      if (topMusicalIds?.length === 0) {
        // Fallback to just getting active musicals
        const { data: allMusicals, error: allError } = await supabase?.from('musicals')?.select('id, title, poster_url, genre, year_premiered, description, composer')?.eq('is_active', true)?.order('created_at', { ascending: false })?.limit(limit);

        if (allError) throw allError;

        return allMusicals?.map(musical => ({
          id: musical?.id,
          title: musical?.title,
          posterUrl: musical?.poster_url,
          genre: musical?.genre,
          yearPremiered: musical?.year_premiered,
          description: musical?.description,
          composer: musical?.composer
        })) || [];
      }

      const { data: musicals, error: musicalsError } = await supabase?.from('musicals')?.select('id, title, poster_url, genre, year_premiered, description, composer')?.in('id', topMusicalIds)?.eq('is_active', true)?.limit(limit);

      if (musicalsError) throw musicalsError;

      return musicals?.map(musical => ({
        id: musical?.id,
        title: musical?.title,
        posterUrl: musical?.poster_url,
        genre: musical?.genre,
        yearPremiered: musical?.year_premiered,
        description: musical?.description,
        composer: musical?.composer
      })) || [];
    } catch (error) {
      console.error('Error fetching popular musicals:', error);
      throw error;
    }
  },

  /**
   * Get recently updated musicals
   */
  async getRecentlyUpdatedMusicals(limit = 12) {
    try {
      const { data, error } = await supabase?.from('musicals')?.select('id, title, poster_url, genre, year_premiered, description, composer, updated_at')?.eq('is_active', true)?.order('updated_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return data?.map(musical => ({
        id: musical?.id,
        title: musical?.title,
        posterUrl: musical?.poster_url,
        genre: musical?.genre,
        yearPremiered: musical?.year_premiered,
        description: musical?.description,
        composer: musical?.composer
      })) || [];
    } catch (error) {
      console.error('Error fetching recently updated musicals:', error);
      throw error;
    }
  },

  /**
   * Get recently added musicals
   */
  async getRecentlyAddedMusicals(limit = 12) {
    try {
      const { data, error } = await supabase?.from('musicals')?.select('id, title, poster_url, genre, year_premiered, description, composer, created_at')?.eq('is_active', true)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return data?.map(musical => ({
        id: musical?.id,
        title: musical?.title,
        posterUrl: musical?.poster_url,
        genre: musical?.genre,
        yearPremiered: musical?.year_premiered,
        description: musical?.description,
        composer: musical?.composer
      })) || [];
    } catch (error) {
      console.error('Error fetching recently added musicals:', error);
      throw error;
    }
  },

  /**
   * Search musicals
   */
  async searchMusicals(query) {
    try {
      const { data, error } = await supabase?.from('musicals')?.select('id, title, poster_url, genre, year_premiered, description, composer')?.eq('is_active', true)?.ilike('title', `%${query}%`)?.order('title', { ascending: true })?.limit(20);

      if (error) throw error;

      return data?.map(musical => ({
        id: musical?.id,
        title: musical?.title,
        posterUrl: musical?.poster_url,
        genre: musical?.genre,
        yearPremiered: musical?.year_premiered,
        description: musical?.description,
        composer: musical?.composer
      })) || [];
    } catch (error) {
      console.error('Error searching musicals:', error);
      throw error;
    }
  }
};